import type { FC } from 'react'
import { useState } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useAdminAccess } from '@/hooks/access'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { LogSmsPush } from '@/modules/admin/resources'

export const SMSLogReceiversListModal: FC<{ log: LogSmsPush }> = ({ log }) => {
  useAdminAccess()

  const [showWholeMessage, setShowWholeMessage] = useState(false)

  const { isLoading, isError, data } = useQuery(
    // Modal does not exist if batchId is null
    queries.adminSMSLogsListByBatch(log.batchId!),
    () => adminResources.getSMSLogReceiversList(log.batchId!),
    {
      enabled: Boolean(log.batchId),
    }
  )

  const { visibleList, ListLoaderComponent } = useRenderLimit(data || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError) {
    return <NoResults />
  }

  return (
    <div>
      <div className="mb-7 flex flex-col gap-2">
        <div>
          <span className="font-bold">
            <Translation id="sender: " />
          </span>
          <span>{log.sender}</span>
        </div>
        <div>
          <span className="font-bold">
            <Translation id="type: " />
          </span>
          <Translation id={log.isPrivate ? 'private' : 'public'} />
        </div>
        <div
          className="flex gap-1 hover:cursor-pointer"
          onClick={() => setShowWholeMessage(!showWholeMessage)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              setShowWholeMessage(!showWholeMessage)
            }
          }}
        >
          <span className="font-bold">
            <Translation id="message: " />
          </span>
          <div className={showWholeMessage ? 'break-all' : 'truncate'}>{log.message}</div>
        </div>
      </div>
      <ResultCount count={data.length} />

      <div className="max-h-[35rem] overflow-y-auto">
        <Table>
          <thead>
            <tr>
              <th>
                <Translation id="receiver" />
              </th>

              <th>
                <Translation id="response" />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map(item => (
              <tr className="pointer-events-none" key={item.id}>
                <Td>{item.receiver}</Td>
                <Td>{item.response}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <ListLoaderComponent />
      </div>
    </div>
  )
}
